/**
 * Vendors ´Cookie consent > Tools > Matomo´
 */

import {noop} from '@ifabnpm/bootstrap/utils.js';
import {load} from '@ifabnpm/bootstrap/utils/load.js';

import BaseClass from '@ifabnpm/bootstrap/utils/base-class.js';

/**
 * @type {string}
 */
const NAME = 'matomo';

/**
 * @type {string}
 */
const VERSION = '1.0.0';

/**
 *
 * @type {Object}
 */
const DEFAULT = {
	siteId: 0,
	trackerDomain: '',
	trackerFile: 'matomo.php',
	trackerFileJs: 'matomo.js',
	onDisable: noop,
	onEnable: noop,

	// Argumente `_paq.push` (verwendet bei Initialisierung)
	setCookieDomain: window.location.hostname,
	setSecureCookie: true,
	setVisitorCookieTimeout: 2880, // 2 Tage
	setReferralCookieTimeout: 2800, // 2 Tage
	setSessionCookieTimeout: 0, // Session only

	// Argumente `_paq.push` (verwendet bei Deaktivierung)
	// Value = '', dann wird nur der Schlüsselname übermittelt
	// Value = null, dann wird kein Schlüsselname übermittelt
	// Value != '', dann wird der Schlüsselname inkl. Value übermittelt
	paramsWhenInit: {
		// requireConsent      : '',
		// // requireCookieConsent: '',
		// trackPageView       : '',
		// enableLinkTracking  : '',
		// setDoNotTrack       : true
	},

	// Argumente `_paq.push` (verwendet bei Deaktivierung)
	// Value = '', dann wird nur der Schlüsselname übermittelt
	// Value = null, dann wird kein Schlüsselname übermittelt
	// Value != '', dann wird der Schlüsselname inkl. Value übermittelt
	paramsWhenDisable: {
		disableCookies          : '',
		deleteCookies           : '',
		forgetConsentGiven      : '',
		// forgetCookieConsentGiven: '',
		optUserOut              : ''
	},

	// Argumente `_paq.push` (verwendet bei Aktivierung)
	// Value = '', dann wird nur der Schlüsselname übermittelt
	// Value = null, dann wird kein Schlüsselname übermittelt
	// Value != '', dann wird der Schlüsselname inkl. Value übermittelt
	paramsWhenEnable: {
		setConsentGiven      : '',
		// setCookieConsentGiven: '',
		enableHeartBeatTimer : 30
	},

	autoEnableWhenReady : false
};

/**
 *  Class
 */
class Matomo extends BaseClass {
	/**
	 * @param {HTMLElement|Node} [element=null]
	 * @param {Object} [config={}]
	 */
	constructor(element = null, config = {}) {
		super(element, config);

		this.isReady = false;

		if (this._config.siteId && this._config.trackerDomain) {
			this.isReady = true;

			window._paq = window._paq || [];

			if (this._config.trackerDomain.slice(-1) !== '/') {
				this._config.trackerDomain = `${this._config.trackerDomain}/`;
			}

			window._paq.push(
				['setCookieDomain', this._config.setCookieDomain],
				['setSecureCookie', this._config.setSecureCookie],
				['setVisitorCookieTimeout', this._config.setVisitorCookieTimeout],
				['setReferralCookieTimeout', this._config.setReferralCookieTimeout],
				['setSessionCookieTimeout', this._config.setSessionCookieTimeout]
			);

			for (const [key, value] of Object.entries(this._config.paramsWhenInit)) {
				if (value === '') {
					window._paq.push([key]);
				} else if (value !== null) {
					window._paq.push([key, value]);
				}
			}

			if (this._config.autoEnableWhenReady) {
				this.enable();
			}
		}
	}

	/**
	 * Matomo aktivieren
	 */
	enable() {
		if (this.isReady) {
			window._paq.push(
				['setTrackerUrl', `${this._config.trackerDomain}${this._config.trackerFile}`],
				['setSiteId', this._config.siteId]
			);

			for (const [key, value] of Object.entries(this._config.paramsWhenEnable)) {
				if (value === '') {
					window._paq.push([key]);
				} else if (value !== null) {
					window._paq.push([key, value]);
				}
			}

			load(`${this._config.trackerDomain}${this._config.trackerFileJs}`).then(() => {
				this._config.onEnable();
			});
		}
	}

	/**
	 * Matomo deaktivieren
	 */
	disable() {
		if (this.isReady) {
			for (const [key, value] of Object.entries(this._config.paramsWhenDisable)) {
				if (value === '') {
					window._paq.push([key]);
				} else if (value !== null) {
					window._paq.push([key, value]);
				}
			}

			this._config.onDisable();
		}
	}

	// Static ------------------------------------------------------------------

	// Getters -----------------------------------------------------------------

	/**
	 * @returns {string}
	 * @constructor
	 */
	static get VERSION() {
		return VERSION;
	}

	/**
	 * @returns {Object}
	 * @constructor
	 */
	static get Default() {
		return DEFAULT;
	}

	/**
	 * @returns {string}
	 * @constructor
	 */
	static get NAME() {
		return NAME;
	}

	/**
	 * @returns {string}
	 * @constructor
	 */
	static get DATA_KEY() {
		return `ifab.${this.NAME}`;
	}

	/**
	 * @returns {string}
	 * @constructor
	 */
	static get EVENT_KEY() {
		return `.${this.DATA_KEY}`;
	}
}

// Export
export default Matomo;
